/**
 * Kit de gráficos SVG, escrito a mano.
 *
 * Escrito a mano y no con una librería por una razón concreta: §5.3 exige una forma de gráfico
 * muy específica —la media móvil de 7 días como serie principal y los puntos crudos en gris de
 * fondo— y las librerías de charts pelean contra eso. Acá esa forma es el default y no hay que
 * configurarla en cada uso.
 *
 * El SVG se dibuja al ancho REAL del contenedor, medido con ResizeObserver, en vez de escalar un
 * viewBox fijo. Escalar deforma el texto de los ejes —los números salen aplastados y se vuelven
 * ilegibles— y en una app cuyo punto es leer números eso es inaceptable.
 *
 * Sin relleno de área, sin degradados, sin animación de entrada. Y sin línea con menos de tres
 * lecturas: dos puntos siempre forman una recta perfecta, y esa recta dice una tendencia que el
 * dato todavía no tiene.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatearNumero } from '@mg/core';

export interface PuntoGrafico {
  fecha: string;
  valor: number;
  media: number;
  n: number;
}

/** Ancho real del contenedor. Devuelve 0 hasta la primera medición. */
function useAncho(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [ancho, setAncho] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entradas) => {
      const w = entradas[0]?.contentRect.width ?? 0;
      // Redondeo a entero: un ancho fraccionario deja los trazos de 1 px borrosos.
      setAncho(Math.floor(w));
    });
    ro.observe(el);
    setAncho(Math.floor(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);

  return [ref, ancho];
}

const fechaCorta = (f: string): string => `${f.slice(8, 10)}/${f.slice(5, 7)}`;

function escalaY(valores: number[], alto: number, sup: number, inf: number) {
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  // Un rango cero (todas las lecturas iguales) dejaría la línea pegada al borde: se abre a mano.
  const rango = max - min < 1e-9 ? Math.max(1, Math.abs(max) * 0.02) : max - min;
  const colchon = rango * 0.12;
  const y0 = min - colchon;
  const y1 = max + colchon;
  return {
    y0,
    y1,
    aY: (v: number) => sup + (1 - (v - y0) / (y1 - y0)) * (alto - sup - inf),
  };
}

export function LineaConMedia({
  puntos,
  alto = 160,
  tono = 'tiza',
  unidad = '',
  mostrarCrudos = true,
  decimales = 1,
}: {
  puntos: PuntoGrafico[];
  alto?: number;
  tono?: 'tiza' | 'agua' | 'ambar';
  unidad?: string;
  mostrarCrudos?: boolean;
  decimales?: number;
}): ReactNode {
  const [ref, ancho] = useAncho();

  const contenido = (): ReactNode => {
    if (puntos.length === 0) {
      return (
        <p className="nota--fina" style={{ height: alto, display: 'grid', placeItems: 'center', margin: 0 }}>
          Todavía no hay lecturas.
        </p>
      );
    }
    if (ancho < 60) return <div style={{ height: alto }} />;

    const izq = 38;
    const der = 8;
    const sup = 10;
    const inf = 18;

    const { y0, y1, aY } = escalaY(
      puntos.flatMap((p) => [p.valor, p.media]),
      alto,
      sup,
      inf,
    );

    const aX = (i: number) =>
      puntos.length === 1 ? izq + (ancho - izq - der) / 2 : izq + (i / (puntos.length - 1)) * (ancho - izq - der);

    const linea = puntos
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${aX(i).toFixed(1)},${aY(p.media).toFixed(1)}`)
      .join(' ');

    const primera = puntos[0]!;
    const ultima = puntos[puntos.length - 1]!;

    /**
     * Con menos de tres lecturas no se dibuja línea. Dos puntos SIEMPRE forman una recta perfecta,
     * y con 110 y 96 esa recta se lee como "bajé 14 kg sostenido" cuando lo único que hay son dos
     * pesadas: la media de 7 días todavía no tiene de dónde salir. Se marcan los puntos y se dice
     * cuántos son, que es lo mismo que ya hacía el panel de una sola lectura.
     */
    const sinTendencia = puntos.length < 3;
    const pieSinTendencia = `${puntos.length} ${puntos.length === 1 ? 'lectura' : 'lecturas'} · sin tendencia`;

    return (
      <svg
        className="grafico"
        width={ancho}
        height={alto}
        viewBox={`0 0 ${ancho} ${alto}`}
        role="img"
        aria-label={
          sinTendencia
            ? `${pieSinTendencia}. Hacen falta 3 para dibujarla.`
            : `Tendencia de ${formatearNumero(primera.media, decimales)} a ${formatearNumero(
                ultima.media,
                decimales,
              )} ${unidad}`
        }
      >
        {[y1, (y0 + y1) / 2, y0].map((v, i) => (
          <g key={i}>
            <line className="g-rejilla" x1={izq} x2={ancho - der} y1={aY(v)} y2={aY(v)} />
            <text className="g-etiqueta" x={izq - 5} y={aY(v) + 3.5} textAnchor="end">
              {formatearNumero(v, decimales)}
            </text>
          </g>
        ))}

        {mostrarCrudos &&
          puntos.map((p, i) => <circle key={`c-${i}`} className="g-crudo" cx={aX(i)} cy={aY(p.valor)} r={3} />)}

        {!sinTendencia && <path className={tono === 'tiza' ? 'g-media' : `g-media g-media--${tono}`} d={linea} />}

        {sinTendencia &&
          puntos.map((p, i) => (
            <circle
              key={`p-${i}`}
              className={tono === 'tiza' ? 'g-punto' : `g-punto g-punto--${tono}`}
              cx={aX(i)}
              cy={aY(p.media)}
              r={4}
            />
          ))}

        {!sinTendencia && (
          <>
            <text className="g-etiqueta" x={izq} y={alto - 5}>
              {fechaCorta(primera.fecha)}
            </text>
            <text className="g-etiqueta" x={ancho - der} y={alto - 5} textAnchor="end">
              {fechaCorta(ultima.fecha)}
            </text>
          </>
        )}

        {/* El pie va corto a la fuerza: `.grafico` es overflow visible, y en la rejilla de tres
            gráficos del desktop cada panel mide ~250 px. La frase larga que había acá se desbordaba
            encima del gráfico de al lado y de su eje, y los tres textos se leían como una sola línea
            rota. Por eso no lleva ni las fechas ni "hacen falta 3": eso va en el aria-label. */}
        {sinTendencia && (
          <text className="g-etiqueta" x={ancho / 2} y={alto - 5} textAnchor="middle">
            {pieSinTendencia}
          </text>
        )}
      </svg>
    );
  };

  return <div ref={ref}>{contenido()}</div>;
}

/** Barras por período. La barra en ámbar es la que alcanzó el objetivo. */
export function Barras({
  datos,
  alto = 140,
  objetivo,
  unidad = '',
}: {
  datos: { etiqueta: string; valor: number }[];
  alto?: number;
  objetivo?: number | null;
  unidad?: string;
}): ReactNode {
  const [ref, ancho] = useAncho();

  const contenido = (): ReactNode => {
    if (datos.length === 0) {
      return (
        <p className="nota--fina" style={{ height: alto, display: 'grid', placeItems: 'center', margin: 0 }}>
          Sin datos en el período.
        </p>
      );
    }
    if (ancho < 60) return <div style={{ height: alto }} />;

    const izq = 38;
    const der = 8;
    const sup = 10;
    const inf = 20;
    const max = Math.max(...datos.map((d) => d.valor), objetivo ?? 0, 1);
    const aY = (v: number) => sup + (1 - v / (max * 1.1)) * (alto - sup - inf);
    const anchoCol = (ancho - izq - der) / datos.length;

    // Con muchas barras no caben todas las etiquetas: se saltean, pero se dice cuántas hay.
    const cada = Math.ceil((datos.length * 34) / Math.max(1, ancho - izq - der));

    return (
      <svg
        className="grafico"
        width={ancho}
        height={alto}
        viewBox={`0 0 ${ancho} ${alto}`}
        role="img"
        aria-label={`${datos.length} períodos, máximo ${formatearNumero(max)} ${unidad}`}
      >
        <text className="g-etiqueta" x={izq - 5} y={aY(max) + 3.5} textAnchor="end">
          {formatearNumero(max, 0)}
        </text>
        <line className="g-rejilla" x1={izq} x2={ancho - der} y1={alto - inf} y2={alto - inf} />

        {objetivo != null && objetivo > 0 && (
          <line className="g-rejilla" x1={izq} x2={ancho - der} y1={aY(objetivo)} y2={aY(objetivo)} />
        )}

        {datos.map((d, i) => {
          const y = aY(d.valor);
          const alcanzo = objetivo != null && objetivo > 0 && d.valor >= objetivo;
          return (
            <g key={`${d.etiqueta}-${i}`}>
              <rect
                className={alcanzo ? 'g-barra g-barra--objetivo' : 'g-barra'}
                x={izq + i * anchoCol + anchoCol * 0.18}
                y={y}
                width={Math.max(2, anchoCol * 0.64)}
                height={Math.max(1, alto - inf - y)}
                rx={1}
              />
              {i % cada === 0 && (
                <text
                  className="g-etiqueta"
                  x={izq + i * anchoCol + anchoCol / 2}
                  y={alto - 5}
                  textAnchor="middle"
                >
                  {d.etiqueta}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  return <div ref={ref}>{contenido()}</div>;
}
